import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Campaign Audience ---

export interface CampaignAudience {
  filter_industry: string | null;
  filter_source: string | null;
  filter_status: string | null;
  filter_tags: string[] | null;
  estimated_count: number;
}

// --- Campaign Schedule ---

export interface CampaignSchedule {
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

// --- Campaign Tracking ---

export interface CampaignTracking {
  total_recipients: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  unsubscribed_count: number;
}

// --- Marketing Campaign Document ---

export interface MarketingCampaign {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  type: string;
  status: string;
  target_audience: CampaignAudience;
  schedule: CampaignSchedule;
  tracking: CampaignTracking;
  owner_id: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Campaign Recipient Document ---

export interface CampaignRecipient {
  id: string;
  tenant_id: string;
  campaign_id: string;
  contact_id: string;
  status: string;
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  unsubscribed_at: string | null;
  error_message: string;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Create/Update DTOs ---

export interface CampaignAudienceDto {
  filter_industry?: string;
  filter_source?: string;
  filter_status?: string;
  filter_tags?: string[];
}

export interface CreateCampaignDto {
  name: string;
  description?: string;
  type: string;
  target_audience?: CampaignAudienceDto;
  scheduled_at?: string;
  owner_id?: string;
  tags?: string[];
}

export interface UpdateCampaignDto {
  name: string;
  description?: string;
  type: string;
  status?: string;
  target_audience?: CampaignAudienceDto;
  scheduled_at?: string;
  owner_id?: string;
  tags?: string[];
}

export interface CreateCampaignRecipientDto {
  contact_id: string;
}

export interface UpdateCampaignRecipientDto {
  status: string;
  error_message?: string;
}

// --- Filter Types ---

export interface CampaignFilters extends SearchParams {
  type?: string;
  status?: string;
  owner_id?: string;
}

export interface RecipientFilters extends SearchParams {
  status?: string;
}

// --- Analytics Response Types ---

export interface CampaignSummaryResponse {
  campaign_id: string;
  campaign_name: string;
  status: string;
  type: string;
  total_recipients: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  unsubscribed_count: number;
  send_rate: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
}

export interface AggregationBucket {
  key: string;
  doc_count: number;
  value?: number;
}

export interface TimeSeriesBucket {
  date: string;
  doc_count: number;
  value?: number;
}

export interface CampaignOverviewResponse {
  total_campaigns: number;
  status_breakdown: AggregationBucket[];
  type_breakdown: AggregationBucket[];
  recipient_status_breakdown: AggregationBucket[];
}

export interface CampaignTrendResponse {
  monthly_trend: TimeSeriesBucket[];
}

// ========== Campaign API Functions ==========

export async function getCampaigns(params?: CampaignFilters) {
  return apiGet<PagedResponse<MarketingCampaign>>('/api/v1/campaigns', params);
}

export async function getCampaign(id: string) {
  return apiGet<MarketingCampaign>(`/api/v1/campaigns/${id}`);
}

export async function createCampaign(data: CreateCampaignDto) {
  return apiPost<MarketingCampaign>('/api/v1/campaigns', data);
}

export async function updateCampaign(id: string, data: UpdateCampaignDto) {
  return apiPut<MarketingCampaign>(`/api/v1/campaigns/${id}`, data);
}

export async function deleteCampaign(id: string) {
  return apiDelete<void>(`/api/v1/campaigns/${id}`);
}

// ========== Campaign Lifecycle ==========

export async function launchCampaign(id: string) {
  return apiPost<MarketingCampaign>(`/api/v1/campaigns/${id}/launch`);
}

export async function pauseCampaign(id: string) {
  return apiPost<MarketingCampaign>(`/api/v1/campaigns/${id}/pause`);
}

export async function resumeCampaign(id: string) {
  return apiPost<MarketingCampaign>(`/api/v1/campaigns/${id}/resume`);
}

// ========== Recipient API Functions ==========

export async function getRecipients(campaignId: string, params?: RecipientFilters) {
  return apiGet<PagedResponse<CampaignRecipient>>(`/api/v1/campaigns/${campaignId}/recipients`, params);
}

export async function addRecipient(campaignId: string, data: CreateCampaignRecipientDto) {
  return apiPost<CampaignRecipient>(`/api/v1/campaigns/${campaignId}/recipients`, data);
}

export async function updateRecipient(campaignId: string, recipientId: string, data: UpdateCampaignRecipientDto) {
  return apiPut<CampaignRecipient>(`/api/v1/campaigns/${campaignId}/recipients/${recipientId}`, data);
}

export async function deleteRecipient(campaignId: string, recipientId: string) {
  return apiDelete<void>(`/api/v1/campaigns/${campaignId}/recipients/${recipientId}`);
}

// ========== Analytics API Functions ==========

export async function getCampaignSummary(campaignId: string) {
  return apiGet<CampaignSummaryResponse>(`/api/v1/campaign-analytics/${campaignId}/summary`);
}

export async function getCampaignOverview(params?: { date_from?: string; date_to?: string; type?: string }) {
  return apiGet<CampaignOverviewResponse>('/api/v1/campaign-analytics/overview', params);
}

export async function getCampaignTrend(params?: { date_from?: string; date_to?: string; type?: string }) {
  return apiGet<CampaignTrendResponse>('/api/v1/campaign-analytics/trend', params);
}
